import sha1 from 'js-sha1';
import { filter as filterAsync, map as mapAsync } from 'p-iteration';
import each from 'lodash/forEach';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNaN from 'lodash/isNaN';
import set from 'lodash/set';
import size from 'lodash/size';
import includes from 'lodash/includes';
import type {
  UploadFileWithAPIOptions,
  UploadItem,
} from 'box-ui-elements/es/common/types/upload';
import { getFile } from 'box-ui-elements/es/utils/uploads';
import {
  TYPE_GROUP,
  TYPE_TEXT_FIELD,
  TYPE_SELECT_FIELD,
  TYPE_TAXONOMY_FIELD,
  TYPE_UPLOAD_FIELD,
  TYPE_DATE_FIELD,
  TEXTTYPE_EMAIL,
  TEXTTYPE_NUMBER,
  REGEX_EMAIL_VALIDATION,
  REGEX_UNICODE_BMP_VALIDATION,
  TYPE_CHECKBOX_FIELD,
  TYPE_RADIO_FIELD,
  TYPE_NUMBER_FIELD,
  FORM_TYPE_FORM,
  FORM_TYPE_FILE_REQUEST,
  DEFAULT_MAX_SELECTIONS,
} from '../../constants';
import { ERRORS, type FormError } from './FormErrors';

export type FormValidationError = FormError & {
  notificationOverride?: FormError;
};

/**
 * This type is not efficient in the current implementation.
 * We keep it as is for now to avoid changing the structure of the errors object.
 * A more efficient approach would be to separate notification overrides from field errors.
 */
export type FormValidationErrors = Record<string, FormError> & {
  notificationOverride?: FormError;
};

const isEmailValid = (email: string) =>
  REGEX_EMAIL_VALIDATION.test(String(email).toLowerCase());

const isNumeric = (n: string) => !isNaN(Number(n) - parseFloat(n));

const hasSpecialChars = (text: string) =>
  REGEX_UNICODE_BMP_VALIDATION.test(String(text));

export const validateTextField = (
  textType: string,
  required: boolean,
  value: string,
): FormValidationError | undefined => {
  if (required && isEmpty(value)) {
    return ERRORS.REQUIRED_FIELD;
  }
  if (!isEmpty(value)) {
    switch (textType) {
      case TEXTTYPE_EMAIL:
        if (!isEmailValid(value)) {
          return ERRORS.INVALID_EMAIL_ADDRESS;
        }
        break;
      case TEXTTYPE_NUMBER:
        if (!isNumeric(value)) {
          return ERRORS.NOT_A_NUMBER;
        }
        break;
      default:
        if (hasSpecialChars(value)) {
          const obj = {
            ...ERRORS.SPECIAL_CHARS,
            notificationOverride: ERRORS.SPECIAL_CHARS_NOTIFICATION,
          } as const;
          return obj;
        }
        break;
    }
  }
  return undefined;
};

export const validateNumberField = (
  required: boolean,
  value: string,
): FormValidationError | undefined => {
  if (required && isEmpty(value)) {
    return ERRORS.REQUIRED_NUMBER_FIELD;
  }
  if (!isEmpty(value) && !isNumeric(value)) {
    return ERRORS.NOT_A_NUMBER;
  }
  return undefined;
};

export const validateSelectField = (
  required: boolean,
  values: Array<string>,
): FormValidationError | undefined => {
  if (required && size(values) === 0) {
    return ERRORS.REQUIRED_FIELD;
  }
  return undefined;
};

export const validateTaxonomyField = validateSelectField;

export const validateMaximumSelections = (
  maximumSelections: number,
  values: Array<string>,
): FormValidationError | undefined => {
  if (
    maximumSelections !== DEFAULT_MAX_SELECTIONS &&
    size(values) > maximumSelections
  ) {
    return {
      ...ERRORS.MAXIMUM_SELECTIONS_EXCEEDED,
      notificationOverride: ERRORS.FORM_GENERIC_ERROR_MESSAGE,
    };
  }
  return undefined;
};

export const validateListField = (
  required: boolean,
  values: Array<string>,
): FormValidationError | undefined => {
  if (required && size(values) === 0) {
    return ERRORS.REQUIRED_LIST_FIELD;
  }
  return undefined;
};

export const validateDateField = (
  required: boolean,
  value: string,
): FormValidationError | undefined => {
  if (required && isEmpty(value)) {
    return ERRORS.REQUIRED_FIELD;
  }
  return undefined;
};

export const validateUploadField = (
  required: boolean,
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  values: Array<any>,
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2749
  type: FORM_TYPE_FORM | FORM_TYPE_FILE_REQUEST = FORM_TYPE_FILE_REQUEST,
): FormValidationError | undefined => {
  if (required && size(values) === 0) {
    const uploadError =
      type === FORM_TYPE_FORM
        ? ERRORS.REQUIRED_FORM_UPLOAD_FIELD
        : ERRORS.REQUIRED_UPLOAD_FIELD;

    return uploadError;
  }
  return undefined;
};

/**
 * Determines if an error is related to file upload description validation.
 *
 * At the moment, all validation errors are stored by field ID.
 * Since the file upload field includes 2 controls (file uploader and description),
 * this function is used to determine if an error is related to the description
 * and to keep it in sync with validateDescriptionForUploadField.
 */
export const isFileUploadDescriptionValidationError = (errorId: string) => {
  return (
    errorId === ERRORS.REQUIRED_FILE_DESCRIPTION.id ||
    errorId === ERRORS.SPECIAL_CHARS.id
  );
};

export const validateDescriptionForUploadField = (
  showFileDescription: boolean,
  required: boolean,
  value: string,
): FormValidationError | undefined => {
  if (!showFileDescription) return undefined;

  if (required && isEmpty(value)) {
    return ERRORS.REQUIRED_FILE_DESCRIPTION;
  }

  if (hasSpecialChars(value)) {
    return {
      ...ERRORS.SPECIAL_CHARS,
      notificationOverride: ERRORS.SPECIAL_CHARS_NOTIFICATION,
    };
  }

  return undefined;
};

export const filterFilesNotAlreadyAdded = async (
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2709
  items: Array<UploadFileWithAPIOptions | File>,
  // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2709
  itemsAlreadyInQueue: Array<UploadItem>,
) => {
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  const newItems: Array<File | any> = [];
  const sameName: Array<
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    | any
    | {
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        item: File | any;
        matcher: string;
      }
  > = [];

  // filter on size/name equality first as a quick pass

  const nameSizeAlreadyInQueue = itemsAlreadyInQueue.map(
    ({ name, size: fileSize }) => `${name}|${fileSize}`,
  );

  items.forEach(item => {
    const { name, size: fileSize } = getFile(item);
    if (includes(nameSizeAlreadyInQueue, `${name}|${fileSize}`)) {
      sameName.push({ item, matcher: `${name}|${fileSize}` });
    } else {
      newItems.push(item);
    }
  });

  if (sameName.length === 0) return newItems;

  // hash the matching files, and compare shas to determine equality

  const readFile = (blob: Blob) => {
    const reader = new window.FileReader();
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    return new Promise((resolve, reject: (error?: any) => void) => {
      reader.readAsArrayBuffer(blob);
      reader.onload = () => {
        resolve({
          buffer: reader.result,
          readCompleteTimestamp: Date.now(),
        });
      };
      reader.onerror = reject;
    });
  };
  const getSHA = async (file: File) => {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2339
    const { buffer } = await readFile(file);
    return sha1(buffer);
  };

  const sameItemsAlreadyInQueue = itemsAlreadyInQueue.filter(
    ({ name, size: fileSize }) =>
      includes(
        sameName.map(item => item.matcher),
        `${name}|${fileSize}`,
      ),
  );

  const sameShasAlreadyInQueue = await mapAsync(
    sameItemsAlreadyInQueue,
    async item => {
      const { file, name, fileSize } = item;
      try {
        return await getSHA(file);
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
      } catch (e: any) {
        // arbitrary just to be used for matching
        return `${name}|${fileSize}`;
      }
    },
  );

  const newSameItems = await filterAsync(sameName, async ({ item }) => {
    const uploadFile = getFile(item);
    const { name, size: fileSize } = uploadFile;
    let sha;
    try {
      sha = await getSHA(uploadFile);
      /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    } catch (e: any) {
      // arbitrary just to be used for matching
      sha = `${name}|${fileSize}`;
    }
    return !includes(sameShasAlreadyInQueue, sha);
  });
  return [...newItems, ...newSameItems.map(item => item.item)];
};

const setError = (
  id: string,
  errors: FormValidationErrors,
  error: FormValidationError,
): void => {
  const { notificationOverride, ...inlineError } = error;
  set(errors, id, inlineError);
  if (notificationOverride) {
    set(errors, 'notificationOverride', notificationOverride);
  }
};

// @ts-expect-error [MIGRATION-SUPPRESSION]: TS2749
type FORM_TYPES = FORM_TYPE_FILE_REQUEST | FORM_TYPE_FORM;

export const validateFormResponse = (
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  components: any = {},
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  itemQueues: any = {},
  /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  response: any = {},
  formType: FORM_TYPES = FORM_TYPE_FILE_REQUEST,
): FormValidationErrors => {
  const errors: FormValidationErrors = {};
  const validators = {
    [TYPE_GROUP]: [],
    [TYPE_UPLOAD_FIELD]: [
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
      ({ id, required, showFileDescription = false }) =>
        validateDescriptionForUploadField(
          showFileDescription,
          required,
          get(response, `${id}.description`, ''),
        ),
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
      ({ id, required }) =>
        validateUploadField(required, get(itemQueues, `${id}.items`), formType),
    ],
    [TYPE_TEXT_FIELD]: [
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
      ({ id, textType, required }) =>
        validateTextField(textType, required, get(response, id)),
    ],
    [TYPE_SELECT_FIELD]: [
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
      ({ id, maximumSelections }) =>
        validateMaximumSelections(maximumSelections, get(response, id)),
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
      ({ id, required }) => validateSelectField(required, get(response, id)),
    ],
    [TYPE_TAXONOMY_FIELD]: [
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
      ({ id, required }) => validateTaxonomyField(required, get(response, id)),
    ],
    [TYPE_DATE_FIELD]: [
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
      ({ id, required }) => validateDateField(required, get(response, id)),
    ],
    [TYPE_CHECKBOX_FIELD]: [
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
      ({ id, maximumSelections }) =>
        validateMaximumSelections(maximumSelections, get(response, id)),
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
      ({ id, required }) => validateListField(required, get(response, id)),
    ],
    [TYPE_RADIO_FIELD]: [
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
      ({ id, required }) => validateListField(required, get(response, id)),
    ],
    [TYPE_NUMBER_FIELD]: [
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7031
      ({ id, required }) => validateNumberField(required, get(response, id)),
    ],
  } as const;

  each(components, component => {
    const { id, type, visible = true } = component;
    if (!visible) {
      return;
    }
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7053
    const componentValidators = validators[type] || [];
    const error = componentValidators.reduce(
      // Don't try to validate if we already got some error - to prevent overwriting it
      // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
      (acc, validator) => acc || validator(component),
      undefined,
    );
    if (error) {
      setError(id, errors, error);
    }
  });

  return errors;
};
